import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';

import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, filter, map, Observable, startWith, switchMap } from 'rxjs';

import { CountryService } from '@valk-nx/services/countries/src/lib/countries.service';

@Component({
	selector: 'formly-field-company-selected',
	template: `
		@if (selectedCompanyInfo$ | async; as selectedCompanyInfo) {
			<dl class="flex flex-col">
				<dd data-test-id="address-line-1">
					{{ selectedCompanyInfo.street }}
					{{ selectedCompanyInfo.houseNumber }}
				</dd>
				<dd data-test-id="address-line-2">
					{{ selectedCompanyInfo.postalCode }}
					{{ selectedCompanyInfo.city }}
				</dd>
				<dd data-test-id="address-line-3">
					{{ selectedCompanyInfo.country }}
				</dd>
			</dl>
		}
	`,
	imports: [AsyncPipe],
})
// NOTE: this component is strictly coupled to a company dropdown formfield
// Please use companySelectorForm() in types.data.ts when implementing it
export class CompanySelectedComponent
	extends FieldType<FieldTypeConfig>
	implements OnInit
{
	private readonly translate = inject(TranslateService);
	private readonly countryService = inject(CountryService);

	companyDropdownKey: string;
	companies: SelectedCompanyInfo[];
	selectedCompanyInfo$: Observable<SelectedCompanyInfo>;

	ngOnInit(): void {
		this.companies = this.field.props['companies'];
		this.companyDropdownKey = this.field.props['companyDropdownKey'];

		const companySelectorControl = this.form?.get(this.companyDropdownKey);

		if (!companySelectorControl) {
			this.selectedCompanyInfo$ =
				EMPTY as Observable<SelectedCompanyInfo>;
			return;
		}
		this.selectedCompanyInfo$ = companySelectorControl.valueChanges.pipe(
			startWith(companySelectorControl.value),
			map((companyId: string) =>
				this.getCompanyById(companyId, this.companies),
			),
			filter(
				(company): company is SelectedCompanyInfo =>
					company !== undefined,
			),
			switchMap((companyInfo) =>
				this.countryService
					.getCountry(
						companyInfo.country,
						this.translate.getCurrentLang(),
					)
					.pipe(
						map((country) => {
							return { ...companyInfo, country };
						}),
					),
			),
		);
	}

	private getCompanyById(
		companyId: string,
		companies: SelectedCompanyInfo[],
	): SelectedCompanyInfo | undefined {
		if (!companyId || !companies) {
			return undefined;
		}
		return companies.find((comp) => comp.guid === companyId);
	}
}

export interface SelectedCompanyInfo {
	guid: string;
	name: string;
	street: string;
	houseNumber: string;
	postalCode: string;
	city: string;
	country: string;
}
